import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Users, Loader2, Eye, EyeOff, FlaskConical, HelpCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordInput = ({ value, onChange, placeholder, showPassword, onToggle, testId }) => (
  <div className="relative">
    <Input type={showPassword ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder || "••••••••"} required className="pr-10" data-testid={testId} />
    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={onToggle}>
      {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
    </Button>
  </div>
);

const LoginPage = () => {
  const { loginAdmin, loginClient, loginLab } = useAuth();
  const [loading, setLoading] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', password: '' });
  const [clientForm, setClientForm] = useState({ email: '', password: '' });
  const [labForm, setLabForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState({});
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recovering, setRecovering] = useState(false);

  const handleLogin = async (e, type) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (type === 'admin') await loginAdmin(adminForm.email, adminForm.password);
      else if (type === 'client') await loginClient(clientForm.email, clientForm.password);
      else await loginLab(labForm.email, labForm.password);
      toast.success('Connexion réussie');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    setRecovering(true);
    try {
      await axios.post(`${API}/auth/recover-password`, { email: recoveryEmail });
      toast.success('Mot de passe envoyé par email !');
      setRecoveryOpen(false);
      setRecoveryEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setRecovering(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left - Image (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1704455306925-1401c3012117?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3NzA1ODcyMzR8MA&ixlib=rb-4.1.0&q=85)' }}>
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <img src="https://customer-assets.emergentagent.com/job_dental-chat-5/artifacts/vbzuvp2d_1000265186.jpg" alt="JPD Logo" className="w-32 h-32 object-contain mb-6 bg-white rounded-lg p-2" />
          <h1 className="text-4xl font-bold tracking-tight mb-4">Jeff Connect</h1>
          <p className="text-lg text-white/90 max-w-md leading-relaxed">Plateforme de messagerie sécurisée entre cabinets dentaires et laboratoires.</p>
        </div>
      </div>

      {/* Right - Login */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-background min-h-screen lg:min-h-0">
        <div className="w-full max-w-md">
          {/* Mobile header */}
          <div className="lg:hidden text-center mb-6">
            <img src="https://customer-assets.emergentagent.com/job_dental-chat-5/artifacts/vbzuvp2d_1000265186.jpg" alt="JPD Logo" className="w-20 h-20 object-contain mx-auto mb-3" />
            <h1 className="text-xl font-bold text-primary">Jeff P. Dental</h1>
            <p className="text-sm text-muted-foreground">Messagerie sécurisée</p>
          </div>

          <Card className="border-border shadow-sm">
            <CardHeader className="space-y-1 pb-3">
              <CardTitle className="text-xl sm:text-2xl font-bold text-center">Connexion</CardTitle>
              <CardDescription className="text-center text-sm">Choisissez votre type de compte</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="client" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="client" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="client-tab"><Users className="h-3.5 w-3.5" />Client</TabsTrigger>
                  <TabsTrigger value="lab" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="lab-tab"><FlaskConical className="h-3.5 w-3.5" />Labo</TabsTrigger>
                  <TabsTrigger value="admin" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="admin-tab"><Shield className="h-3.5 w-3.5" />Admin</TabsTrigger>
                </TabsList>

                <TabsContent value="client">
                  <form onSubmit={(e) => handleLogin(e, 'client')} className="space-y-4">
                    <div className="space-y-2"><Label>Email</Label><Input type="email" value={clientForm.email} onChange={(e) => setClientForm({...clientForm, email: e.target.value})} placeholder="cabinet@exemple.com" required data-testid="client-email-input" /></div>
                    <div className="space-y-2"><Label>Mot de passe</Label><PasswordInput value={clientForm.password} onChange={(e) => setClientForm({...clientForm, password: e.target.value})} showPassword={showPw.client} onToggle={() => setShowPw({...showPw, client: !showPw.client})} testId="client-password-input" /></div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="client-login-button">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Se connecter</Button>
                    <Button type="button" variant="link" className="w-full text-sm text-muted-foreground" onClick={() => setRecoveryOpen(true)} data-testid="forgot-password-btn"><HelpCircle className="h-3.5 w-3.5 mr-1" />Mot de passe oublié ?</Button>
                    <p className="text-center text-sm text-muted-foreground">Pas encore de compte ? <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">Inscrivez votre cabinet</Link></p>
                  </form>
                </TabsContent>

                <TabsContent value="lab">
                  <form onSubmit={(e) => handleLogin(e, 'lab')} className="space-y-4">
                    <div className="space-y-2"><Label>Email</Label><Input type="email" value={labForm.email} onChange={(e) => setLabForm({...labForm, email: e.target.value})} placeholder="labo@exemple.com" required data-testid="lab-email-input" /></div>
                    <div className="space-y-2"><Label>Mot de passe</Label><PasswordInput value={labForm.password} onChange={(e) => setLabForm({...labForm, password: e.target.value})} showPassword={showPw.lab} onToggle={() => setShowPw({...showPw, lab: !showPw.lab})} testId="lab-password-input" /></div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading} data-testid="lab-login-button">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Se connecter</Button>
                    <Button type="button" variant="link" className="w-full text-sm text-muted-foreground" onClick={() => setRecoveryOpen(true)}><HelpCircle className="h-3.5 w-3.5 mr-1" />Mot de passe oublié ?</Button>
                  </form>
                </TabsContent>

                <TabsContent value="admin">
                  <form onSubmit={(e) => handleLogin(e, 'admin')} className="space-y-4">
                    <div className="space-y-2"><Label>Email administrateur</Label><Input type="email" value={adminForm.email} onChange={(e) => setAdminForm({...adminForm, email: e.target.value})} placeholder="jeff@jeffpdental.com" required data-testid="admin-email-input" /></div>
                    <div className="space-y-2"><Label>Mot de passe</Label><PasswordInput value={adminForm.password} onChange={(e) => setAdminForm({...adminForm, password: e.target.value})} showPassword={showPw.admin} onToggle={() => setShowPw({...showPw, admin: !showPw.admin})} testId="admin-password-input" /></div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-button">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Se connecter</Button>
                  </form>
                </TabsContent>
              </Tabs>
              <div className="mt-4 pt-3 border-t space-y-2">
                <p className="text-xs text-muted-foreground text-center">jeffpdental: +15053011071</p>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <Link to="/aide" className="hover:underline" data-testid="login-link-aide">Aide</Link>
                  <Link to="/mentions-legales" className="hover:underline" data-testid="login-link-mentions">Mentions légales</Link>
                  <Link to="/cgu" className="hover:underline" data-testid="login-link-cgu">CGU</Link>
                  <Link to="/confidentialite" className="hover:underline" data-testid="login-link-confidentialite">Confidentialité</Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Password Recovery Dialog */}
      <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
        <DialogContent className="sm:max-w-md" data-testid="recovery-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe oublié</DialogTitle>
            <DialogDescription>Entrez votre email pour recevoir votre mot de passe. Votre mot de passe ne sera pas modifié.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRecover}>
            <div className="py-4"><Input type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} placeholder="Votre email" required data-testid="recovery-email-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRecoveryOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={recovering} data-testid="recovery-submit-btn">{recovering && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Envoyer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
